import type { CSSProperties } from 'react';
import type { MbtiCode } from './types';

type ThemeVars = CSSProperties & Record<`--${string}`, string>;

const BRAND_BY_CODE: Record<MbtiCode, string> = {
  PALE: '#36A5B9',
  PALZ: '#4CA2A8',
  PACE: '#2F9AE0',
  PACZ: '#3D8BCE',
  PRLE: '#6B8E23',
  PRLZ: '#5F9E7C',
  PRCE: '#2EA58E',
  PRCZ: '#5F9A6B',
  SALE: '#F08A24',
  SALZ: '#D97A6B',
  SACE: '#E2678E',
  SACZ: '#A66ACB',
  SRLE: '#CC6C1D',
  SRLZ: '#B8693C',
  SRCE: '#7E87C9',
  SRCZ: '#6C7CB9',
};

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((c) => c + c).join('')
    : normalized;
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  return [r, g, b];
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mix(hexA: string, hexB: string, ratio: number): string {
  const [ar, ag, ab] = hexToRgb(hexA);
  const [br, bg, bb] = hexToRgb(hexB);
  return rgbToHex(
    ar + (br - ar) * ratio,
    ag + (bg - ag) * ratio,
    ab + (bb - ab) * ratio,
  );
}

function darken(hex: string, ratio: number): string {
  return mix(hex, '#000000', ratio);
}

function lighten(hex: string, ratio: number): string {
  return mix(hex, '#FFFFFF', ratio);
}

export function getDiagnosisThemeVars(code: MbtiCode): ThemeVars {
  const brand = BRAND_BY_CODE[code];
  const brandDark = darken(brand, 0.18);
  const brandCtaDark = darken(brand, 0.28);
  const brandSoft = lighten(brand, 0.84);
  const brandSoft2 = lighten(brand, 0.72);
  const pageTop = lighten(brand, 0.82);
  const pageBottom = lighten(brand, 0.94);
  const cardBorder = lighten(brand, 0.78);
  const [r, g, b] = hexToRgb(brand);

  return {
    '--brand': brand,
    '--brandDark': brandDark,
    '--brandSoft': brandSoft,
    '--brandSoft2': brandSoft2,
    '--brandRgb': `${r}, ${g}, ${b}`,
    '--pageTop': pageTop,
    '--pageBottom': pageBottom,
    '--cardBorder': cardBorder,
    '--gradBrand': `linear-gradient(135deg, ${lighten(brand, 0.05)} 0%, ${brandDark} 100%)`,
    '--gradBrandSoft': `linear-gradient(135deg, ${lighten(brand, 0.9)} 0%, ${brandSoft2} 100%)`,
    '--gradCTA': `linear-gradient(135deg, ${lighten(brand, 0.03)} 0%, ${brandCtaDark} 100%)`,
    '--gradCTABanner': `linear-gradient(90deg, ${lighten(brand, 0.2)} 0%, ${mix(brand, brandDark, 0.4)} 100%)`,
  };
}
